'use client';

import { Container, ButtonV2, ButtonV2Props } from 'components/index';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { isBlingVisible } from 'content/products/ring/stacking/constants';
import styles from './OtherProductsSection.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

type ProductCardType = {
  id: string;
  title: string;
  description: string;
  image: string;
  textColor?: string;
  styling?: {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    descriptionColor?: string;
  };
  cta?: ButtonV2Props;
  ctaAlt?: ButtonV2Props;
};

const v1BasePath = 'web_v2/';

export const OtherProductSection = ({
  className,
  title,
  subtitle,
}: {
  className?: string;
  title: string;
  subtitle: string;
}) => {
  const { region, store } = useRegionLocale();
  const showBling = isBlingVisible(region, store);

  // Define product cards based on region
  const productCards: ProductCardType[] = [
    // Blood Vision
    {
      id: 'blood-vision',
      title: 'Blood Vision',
      description: 'Advanced blood testing of 100+ biomarkers.',
      textColor: '#ffffff',
      styling: {
        descriptionColor: '#ffffff',
      },
      image: `${v1BasePath}ring-buy/other-products-section/bv-product-thumb.png`,
      cta: {
        variant: 'white' as const,
        label: 'Book test',
        href: '/blood-vision/buy',
        'data-buttontype': 'bloodvision_features_products_buy_button',
      },
    },
    // Home Monitor
    {
      id: 'home-monitor',
      title: 'Home Monitor',
      description:
        'A quiet yet powerful sleep monitor to help you rest better.',
      textColor: '#000000',
      styling: {
        descriptionColor: '#000000',
        borderColor: 'rgba(0,0,0,0.1)',
      },
      image: `${v1BasePath}ring-buy/other-products-section/home-product-thumb-2.png`,
      cta: {
        variant: 'blue' as const,
        label: 'Buy now',
        href: '/home/buy/',
        'data-buttontype': 'home_features_products_buy_button',
      },
    },
    // Bling (Stacking Rings) — only shown in approved countries with the store enabled
    ...(showBling
      ? [
          {
            id: 'bling-stacking',
            title: 'Bling',
            description:
              'Ultrahuman Bling rings are designed to pair with your Ring AIR for a look that\'s uniquely yours.',
            textColor: '#000000',
            styling: {
              descriptionColor: '#000000',
              borderColor: 'rgba(0,0,0,0.1)',
            },
            image: `${v1BasePath}ring-buy/stacking_us.png`,
            cta: {
              variant: 'blue' as const,
              label: 'Shop now',
              href: '/shop',
              'data-buttontype': 'bling_stacking_products_shop_button',
            },
          },
        ]
      : []),
    // M1
    // {
    //   id: 'm1',
    //   title: 'Track Glucose with M1',
    //   description:
    //     'Optimize your metabolic fitness with continuous glucose tracking and real-time actionable insights.',
    //   textColor: '#ffffff',
    //   styling: {
    //     descriptionColor: '#ffffff',
    //   },
    //   image: `${v1BasePath}ring-buy/other-products-section/m1-product-thumb.png`,
    //   cta: {
    //     variant: 'blue' as const,
    //     label: 'Buy now',
    //     href: '/pricing/',
    //     'data-buttontype': 'm1_features_products_buy_button',
    //   },
    // },
  ];

  return (
    <section className={cx(styles.root, className)}>
      <Container className={styles.container}>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <div className={styles.cardsContainer}>
          {productCards.map((item) => (
            <div
              key={item.id}
              className={styles.productItem}
              style={
                {
                  ['--pi-shadow']: item.styling?.borderColor
                    ? `inset 0 0 0 1px ${item.styling?.borderColor}`
                    : 'none',
                  ['--pi-bg']: `url(${getCompressedAssetUrl(
                    item.image?.startsWith('/') ? item.image : `/${item.image}`,
                  )})`,
                } as React.CSSProperties
              }
            >
              <div className={styles.productContent}>
                <h3
                  className={styles.productTitle}
                  style={
                    {
                      ['--pt-color']: item.textColor || item.styling?.textColor,
                    } as React.CSSProperties
                  }
                >
                  {item.title}
                </h3>
                <p
                  className={styles.productDescription}
                  style={
                    {
                      ['--pd-color']:
                        item.styling?.descriptionColor || item.textColor,
                    } as React.CSSProperties
                  }
                >
                  {item.description}
                </p>
                {(item.cta || item.ctaAlt) && (
                  <div className={styles.productActions}>
                    {item.cta && (
                      <ButtonV2 variant="blue" {...item.cta}>
                        {item.cta.label}
                      </ButtonV2>
                    )}
                    {item.ctaAlt && (
                      <ButtonV2 variant="transparentLight" {...item.ctaAlt}>
                        {item.ctaAlt.label}
                      </ButtonV2>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};
